// components/NarrativeContent.jsx
import React, { useState, useEffect } from "react";
import scenarioOptionMeta from "../../data/scenarioOptionMeta.json";

// ───────────────────── Custom Native Markdown Renderer ─────────────────────
// Adapted from the provided FoodTab example to render LLM output
const NativeAnalysis = ({ content }) => {
  if (!content) return null;
  if (typeof content !== "string") return null;

  const sectionConfig = [
    {
      id: "overview",
      title: "Overview",
      keywords: ["overview", "summary", "pathway description", "descripcion", "resumen"],
    },
    {
      id: "why",
      title: "Why it matters",
      keywords: ["why it matters", "importance", "relevance", "por que", "importancia", "relevancia"],
    },
    {
      id: "outcomes",
      title: "Expected outcomes",
      keywords: ["expected outcomes", "outcomes", "results", "impact", "resultados", "impacto"],
    },
    {
      id: "tradeoffs",
      title: "Multiple Indicators View",
      keywords: ["trade-offs", "tradeoffs", "trade offs", "risks", "costs", "riesgos", "costos"],
    },
    {
      id: "assumptions",
      title: "Assumptions",
      keywords: ["assumptions", "assumption", "dependencies", "supuestos", "dependencias"],
    },
  ];

  const detectSectionId = (headingText) => {
    const normalized = headingText
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    for (const section of sectionConfig) {
      if (section.keywords.some((keyword) => normalized.includes(keyword))) {
        return section.id;
      }
    }

    return null;
  };

  const sectionBuckets = sectionConfig.reduce((acc, section) => {
    acc[section.id] = [];
    return acc;
  }, {});

  const lines = content.split("\n");
  let activeSection = "overview";

  lines.forEach((line) => {
    const trimmed = line.trim();

    const markdownHeading = trimmed.match(/^#{1,4}\s+(.+)$/);
    const colonHeading = trimmed.match(/^([A-Za-z][A-Za-z\s\-]{2,60}):$/);
    const headingCandidate = markdownHeading?.[1] ?? colonHeading?.[1] ?? null;

    if (headingCandidate) {
      const detected = detectSectionId(headingCandidate);
      if (detected) {
        activeSection = detected;
        return;
      }
    }

    sectionBuckets[activeSection].push(line);
  });

  const hasAnySectionContent = sectionConfig.some((section) =>
    sectionBuckets[section.id].some((line) => line.trim().length > 0)
  );

  if (!hasAnySectionContent) {
    sectionBuckets.overview = [content];
  }

  const parseInline = (text) => {
    const parts = text.split(/(\*\*.*?\*\*|_.*?_)/g);
    return parts.map((part, index) => {
      if (part.startsWith("**") && part.endsWith("**")) {
        return (
          <strong key={index} className="font-bold text-[#323334]">
            {part.slice(2, -2)}
          </strong>
        );
      } else if (part.startsWith("_") && part.endsWith("_")) {
        return (
          <em key={index} className="italic text-[#323334]">
            {part.slice(1, -1)}
          </em>
        );
      }
      return part;
    });
  };

  const renderContentLines = (sectionLines) => {
    const cleaned = [...sectionLines];
    while (cleaned.length && cleaned[0].trim() === "") cleaned.shift();
    while (cleaned.length && cleaned[cleaned.length - 1].trim() === "") cleaned.pop();

    if (cleaned.length === 0) return null;

    return cleaned.map((line, index) => {
      const trimmed = line.trim();
      if (!trimmed) {
        return <div key={`gap-${index}`} className="h-1" />;
      }

      if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
        return (
          <div key={`list-${index}`} className="mb-1 flex items-start">
            <span className="mr-2 mt-0.5 text-[10px] font-bold text-slate-400">●</span>
            <span>{parseInline(trimmed.slice(2))}</span>
          </div>
        );
      }

      return (
        <p key={`line-${index}`} className="mb-1.5">
          {parseInline(trimmed)}
        </p>
      );
    });
  };

  return (
    <div className="space-y-5">
      {sectionConfig.map((section, index) => {
        const sectionLines = sectionBuckets[section.id];
        const hasContent = sectionLines.some((line) => line.trim().length > 0);
        if (!hasContent) return null;

        return (
          <section
            key={section.id}
            className={index === 0 ? "" : "border-t border-slate-200 pt-5"}
          >
            <div className="mb-2">
              <h4 className="text-[13px] font-semibold uppercase tracking-[0.08em] text-slate-500">
                {section.title}
              </h4>
            </div>

            <div className="max-w-none font-sans text-[14px] leading-[1.6] text-slate-600">
              {renderContentLines(sectionLines)}
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default function NarrativaContent({ resultsData }) {

  const first = resultsData ?? {};
  const [pathwayName, pathwayObject] = Object.entries(first)[0] ?? [];
  const scenariosRows = pathwayObject?.scenarios ?? [];

  const normalizeString = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");

  const getScenarioTitle = (scenarioName) => {
    const normalizedTypeKey = normalizeString(scenarioName);
    const titleMap = scenarioOptionMeta._titles || {};
    const matchedTitleKey = Object.keys(titleMap).find((key) => {
      const normalizedKey = normalizeString(key);
      return (
        normalizedKey === normalizedTypeKey ||
        normalizedTypeKey.includes(normalizedKey) ||
        normalizedKey.includes(normalizedTypeKey)
      );
    });
    return titleMap[matchedTitleKey] || scenarioName;
  };

  const getScenarioOptionMeta = (scenarioName) => {
    const normalizedTypeKey = normalizeString(scenarioName);
    if (scenarioOptionMeta[normalizedTypeKey]) return scenarioOptionMeta[normalizedTypeKey];

    const matchedKey = Object.keys(scenarioOptionMeta).find((key) => {
      const normalizedKey = normalizeString(key);
      return (
        normalizedKey === normalizedTypeKey ||
        normalizedTypeKey.includes(normalizedKey) ||
        normalizedKey.includes(normalizedTypeKey)
      );
    });

    return matchedKey ? scenarioOptionMeta[matchedKey] : null;
  };

  const getScenarioOptionLabel = (scenarioName, optionValue) => {
    const meta = getScenarioOptionMeta(scenarioName);
    const valueKey = String(optionValue ?? "");
    return meta?.labels?.[valueKey] || valueKey;
  };

  const mappedScenariosRows = scenariosRows.map((scenario) => {
    const rawLabel = scenario?.label ?? scenario?.id ?? "";
    const rawValue = scenario?.value ?? "";
    return {
      ...scenario,
      label: getScenarioTitle(rawLabel),
      value: getScenarioOptionLabel(rawLabel, rawValue),
    };
  });

  const analysisScenarioPayload = mappedScenariosRows.map(({ label, value }) => ({
    label,
    value,
  }));

  console.log("Mapped Scenarios Rows:", mappedScenariosRows);

  // State for LLM Analysis
  const [narrativeAnalysis, setNarrativeAnalysis] = useState("");
  const [loadingAnalysis, setLoadingAnalysis] = useState(false);

  useEffect(() => {
    const fetchNarrative = async () => {
      if (!pathwayName || !scenariosRows) return;

      setLoadingAnalysis(true);
      try {
        const requestBody = {
          context: "pathway_analysis",
          pathway: pathwayName,
          payload: {
            scenarios: analysisScenarioPayload,
            meta: {
              country: pathwayObject.country,
              date: pathwayObject.date,
              user_id: pathwayObject.user_id,
              pathway_id: pathwayObject.id,
              pathway_name: pathwayName,
            },
          }
        };

        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/chat/fablegpt`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(requestBody),
          }
        );

        let result = {};
        if (response.ok) {
          result = await response.json();
          console.log("Analysis received:", result);
        }

        setNarrativeAnalysis(
          result.pathwayAnalysis || "No description generated."
        );

      } catch (error) {
        console.error("Error fetching narrative description:", error);
        setNarrativeAnalysis(
          "Unable to load pathway description at this time."
        );
      } finally {
        setLoadingAnalysis(false);
      }
    };

    fetchNarrative();
  }, [pathwayName, pathwayObject, scenariosRows]);

  return (
    <div className="rounded-xl bg-white p-8 shadow-sm">
   
      {/* Selection of Scenarios */}
      <h3 className="mb-4 text-[16px] font-medium text-[#323334]">
        Selected scenarios
      </h3>

      <div className="mb-10 overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm">
        {mappedScenariosRows.length === 0 ? (
          <div className="px-4 py-4 text-sm text-slate-500">No scenarios found.</div>
        ) : (
          <table className="w-full table-fixed border-collapse text-[15px]">
            <thead className="bg-[#1E5B4F]/10 text-[13px] font-semibold uppercase tracking-[0.08em] text-slate-700">
              <tr>
                <th className="w-[21%] border-b border-r border-slate-300 px-3 py-2 text-left">Scenario</th>
                <th className="w-[29%] border-b border-r border-slate-300 px-3 py-2 text-left">Selection</th>
                <th className="w-[21%] border-b border-r border-slate-300 px-3 py-2 text-left">Scenario</th>
                <th className="w-[29%] border-b border-slate-300 px-3 py-2 text-left">Selection</th>
              </tr>
            </thead>

            <tbody>
              {Array.from({ length: Math.ceil(mappedScenariosRows.length / 2) }, (_, idx) => {
                const left = mappedScenariosRows[idx * 2];
                const right = mappedScenariosRows[idx * 2 + 1];

                return (
                  <tr
                    key={left?.id ?? `scenario-row-${idx}`}
                    className={`transition-colors ${idx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}`}
                  >
                    <td className="border-r border-b border-slate-300 px-3 py-1.5 align-middle text-[13px] font-bold text-slate-800 break-words">
                      {left?.label ?? ""}
                    </td>

                    <td className="border-r border-b border-slate-300 px-3 py-1.5 align-middle text-[13px] font-normal text-slate-700 break-words">
                      {left?.value ?? ""}
                    </td>

                    <td className="border-r border-b border-slate-300 px-3 py-1.5 align-middle text-[13px] font-bold text-slate-800 break-words">
                      {right?.label ?? ""}
                    </td>

                    <td className="border-b border-slate-300 px-3 py-1.5 align-middle text-[13px] font-normal text-slate-700 break-words">
                      {right?.value ?? ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Description */}
      <h3 className="mb-4 text-[16px] font-medium text-[#323334]">
        Pathway Summary
      </h3>

      <div className="w-full min-h-[100px]">
        {loadingAnalysis ? (
          <div className="flex items-center gap-2 text-[#7E8081] text-sm animate-pulse">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24">
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
                fill="none"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating analysis...
          </div>
        ) : (
          <NativeAnalysis content={narrativeAnalysis} />
        )}
      </div>
    </div>
  );
}
